import { contactRelevance } from './scoring.js';
import { commercialOutcomePoints, isHumanReplySequence } from './performance-learning.js';
import { normalizeCompanyName } from './normalise.js';
function featuresFor(profile) {
    const role = contactRelevance(profile.title);
    const out = [{ key: `category:${profile.crmCategory}`, label: `${profile.crmCategory} historical performance` }];
    if (role >= 75)
        out.push({ key: 'role:strong', label: 'Strong-fit roles have performed well' });
    else if (role >= 60)
        out.push({ key: 'role:relevant', label: 'Relevant roles have performed well' });
    if (profile.emailVerification === 'verified')
        out.push({ key: 'email:verified', label: 'Verified-email prospects have performed well' });
    if (profile.opportunityLinked)
        out.push({ key: 'opportunity:linked', label: 'Opportunity-linked outreach has performed well' });
    return out;
}
function confidence(sample) {
    return sample < 5 ? 'early' : sample < 15 ? 'building' : sample < 30 ? 'medium' : 'strong';
}
function opportunityTargetsCompany(opportunity, companyId, companyName) {
    if (companyId && opportunity.companyId === companyId)
        return true;
    const name = normalizeCompanyName(companyName || '');
    if (!name)
        return false;
    if (normalizeCompanyName(opportunity.companyName || '') === name)
        return true;
    return (opportunity.targetCompanies || []).some(target => normalizeCompanyName(target.name) === name);
}
/**
 * Bounded application-level learning for any prospect profile. The learner uses
 * only real sent outreach and only changes ranking after a directly comparable
 * feature has at least five observations. Fixed safety/fit rules still dominate;
 * opportunity and other hard product rules remain outside this bounded learner.
 */
export function learnedProfileAdjustment(target, sequences, contacts, opportunities) {
    const contactById = new Map(contacts.map(contact => [contact.id, contact]));
    const sent = sequences.filter(sequence => !sequence.archived && !sequence.removedFromSentAt && !sequence.customOutreach && sequence.stage === 'sent');
    if (sent.length < 5)
        return { delta: 0, confidence: 'early', reasons: [], evidence: sent.length, totalSent: sent.length, enoughEvidence: false };
    const targetFeatures = featuresFor(target);
    const targetKeys = new Set(targetFeatures.map(feature => feature.key));
    const buckets = new Map();
    let baselinePoints = 0;
    let baselineSample = 0;
    for (const sequence of sent) {
        const contact = contactById.get(sequence.contactId);
        if (!contact)
            continue;
        const points = commercialOutcomePoints(sequence.outcome);
        baselinePoints += points;
        baselineSample++;
        const linked = !!sequence.opportunityId;
        for (const feature of featuresFor({ title: contact.title, crmCategory: contact.crmCategory, emailVerification: contact.emailVerification, opportunityLinked: linked })) {
            const bucket = buckets.get(feature.key) || { label: feature.label, sample: 0, points: 0, replies: 0 };
            bucket.sample++;
            bucket.points += points;
            if (isHumanReplySequence(sequence))
                bucket.replies++;
            buckets.set(feature.key, bucket);
        }
    }
    const baseline = baselinePoints / Math.max(1, baselineSample);
    let delta = 0;
    let evidence = 0;
    const reasons = [];
    for (const key of targetKeys) {
        const bucket = buckets.get(key);
        if (!bucket)
            continue;
        evidence = Math.max(evidence, bucket.sample);
        if (bucket.sample < 5)
            continue;
        const average = bucket.points / bucket.sample;
        const lift = average - baseline;
        // Commercial learning only nudges ranking. Fit/opportunity/safety rules remain primary.
        const adjustment = Math.max(-4, Math.min(6, Math.round(lift * 2)));
        if (!adjustment)
            continue;
        delta += adjustment;
        if (adjustment >= 2)
            reasons.push(`${bucket.label} · learned from ${bucket.sample} sent`);
        else if (adjustment <= -2)
            reasons.push(`${bucket.label} · weaker historical return signal · ${bucket.sample} sent`);
    }
    return {
        delta: Math.max(-8, Math.min(10, delta)),
        confidence: confidence(evidence),
        reasons: [...new Set(reasons)].slice(0, 3),
        evidence,
        totalSent: sent.length,
        enoughEvidence: evidence >= 5
    };
}
/**
 * Contact wrapper used by saved-CRM recommendations. Opportunity linkage is
 * inferred from the current Radar opportunity set so outcome learning can
 * distinguish opportunity-led outreach from generic outreach.
 */
export function learnedRecommendationAdjustment(target, sequences, contacts, opportunities) {
    const opportunityLinked = opportunities.some(opportunity => opportunityTargetsCompany(opportunity, target.companyId, target.companyName));
    return learnedProfileAdjustment({ title: target.title, crmCategory: target.crmCategory, emailVerification: target.emailVerification, opportunityLinked }, sequences, contacts, opportunities);
}
